import React from "react";
import "./HTML_MAIN_CSS.css";

const HTML_WebStorage = () => {
  return (
    <div className="html-container">
      <h1>HTML Web Storage</h1>

      {/* 1. Introduction */}
      <section className="html-section">
        <h2>1. Introduction</h2>
        <p>
          The <strong>HTML Web Storage API</strong> provides a way to store key/value data directly in a web browser.
          It allows websites to store data locally within the user’s browser without the need for cookies.
        </p>
        <p>
          Web Storage is more secure, faster, and capable of storing larger amounts of data compared to cookies.
        </p>
      </section>

      {/* 2. Types of Web Storage */}
      <section className="html-section">
        <h2>2. Types of Web Storage</h2>
        <ul>
          <li>
            <strong>localStorage:</strong> Stores data with no expiration date. Data remains even after closing the browser.
          </li>
          <li>
            <strong>sessionStorage:</strong> Stores data for only one session. Data is lost when the browser tab is closed.
          </li>
        </ul>
      </section>

      {/* 3. Syntax */}
      <section className="html-section">
        <h2>3. Syntax</h2>
        <pre className="code-block">
          <code>{`// Set item
localStorage.setItem("key", "value");

// Get item
let value = localStorage.getItem("key");

// Remove item
localStorage.removeItem("key");

// Clear all storage
localStorage.clear();`}</code>
        </pre>
        <p>
          The same methods apply to <code>sessionStorage</code> — simply replace <code>localStorage</code> with <code>sessionStorage</code>.
        </p>
      </section>

      {/* 4. Example - localStorage */}
      <section className="html-section">
        <h2>4. Example: localStorage</h2>
        <pre className="code-block">
          <code>{`<!DOCTYPE html>
<html>
<body>

<h2>localStorage Example</h2>

<input type="text" id="username" placeholder="Enter your name">
<button onclick="saveName()">Save</button>
<button onclick="showName()">Show</button>
<p id="output"></p>

<script>
function saveName() {
  let name = document.getElementById("username").value;
  localStorage.setItem("user", name);
  alert("Name saved in localStorage!");
}

function showName() {
  let name = localStorage.getItem("user");
  document.getElementById("output").innerText = "Stored Name: " + name;
}
</script>

</body>
</html>`}</code>
        </pre>
      </section>

      {/* 5. Example - sessionStorage */}
      <section className="html-section">
        <h2>5. Example: sessionStorage</h2>
        <pre className="code-block">
          <code>{`<!DOCTYPE html>
<html>
<body>

<h2>sessionStorage Example</h2>

<input type="text" id="color" placeholder="Enter your favorite color">
<button onclick="saveColor()">Save</button>
<button onclick="showColor()">Show</button>
<p id="display"></p>

<script>
function saveColor() {
  let color = document.getElementById("color").value;
  sessionStorage.setItem("favColor", color);
  alert("Color saved in sessionStorage!");
}

function showColor() {
  let color = sessionStorage.getItem("favColor");
  document.getElementById("display").innerText = "Your color: " + color;
}
</script>

</body>
</html>`}</code>
        </pre>
      </section>

      {/* 6. Storage Events */}
      <section className="html-section">
        <h2>6. Storage Events</h2>
        <p>
          When a change is made to Web Storage (like adding or removing data), the <code>storage</code> event is fired.
          This can be used to track changes across different tabs of the same domain.
        </p>
        <pre className="code-block">
          <code>{`window.addEventListener("storage", function(event) {
  console.log("Key changed:", event.key);
  console.log("Old value:", event.oldValue);
  console.log("New value:", event.newValue);
  console.log("Storage area:", event.storageArea);
});`}</code>
        </pre>
      </section>

      {/* 7. Key Differences */}
      <section className="html-section">
        <h2>7. Key Differences Between localStorage and sessionStorage</h2>
        <table className="styled-table">
          <thead>
            <tr>
              <th>Feature</th>
              <th>localStorage</th>
              <th>sessionStorage</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>Storage Duration</td>
              <td>Persists even after browser is closed</td>
              <td>Cleared when tab or window is closed</td>
            </tr>
            <tr>
              <td>Scope</td>
              <td>Available across all tabs/windows of the same origin</td>
              <td>Available only within the same browser tab</td>
            </tr>
            <tr>
              <td>Use Case</td>
              <td>Long-term preferences, user data, offline apps</td>
              <td>Temporary session data</td>
            </tr>
          </tbody>
        </table>
      </section>

      {/* 8. Example Use Cases */}
      <section className="html-section">
        <h2>8. Example Use Cases</h2>
        <ul>
          <li>Saving user settings and preferences</li>
          <li>Storing shopping cart data</li>
          <li>Saving form input temporarily</li>
          <li>Keeping user session data within the same tab</li>
        </ul>
      </section>

      {/* 9. Browser Support */}
      <section className="html-section">
        <h2>9. Browser Support</h2>
        <p>
          Web Storage is supported in all modern browsers, including:
        </p>
        <ul>
          <li>Google Chrome ✅</li>
          <li>Mozilla Firefox ✅</li>
          <li>Microsoft Edge ✅</li>
          <li>Safari ✅</li>
          <li>Opera ✅</li>
        </ul>
      </section>

      {/* 10. Summary */}
      <section className="html-section">
        <h2>10. Summary</h2>
        <ul>
          <li>Use <code>localStorage</code> for long-term data persistence.</li>
          <li>Use <code>sessionStorage</code> for temporary, per-tab data.</li>
          <li>Both store data as key/value pairs and support up to 5MB.</li>
          <li>Web Storage is more secure and efficient than cookies.</li>
        </ul>
      </section>
    </div>
  );
};

export default HTML_WebStorage;
